import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Edit2, Trash2, Search, Upload, Eye, X, Download } from 'lucide-react';
import toast from 'react-hot-toast';
import AppLayout from '../layout/AppLayout';
import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { COLORS } from '../../utils/constants';

const C = COLORS;
const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const TeacherList = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isManager = user?.role === 'manager';
  const isTeacherType = user?.user_type === 'Teacher';
  const hideBulkAndTemplate = isManager || isTeacherType;

  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedTeacher, setSelectedTeacher] = useState(null);
  
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const fetchTeachers = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get('/teachers');
      setTeachers(res.data);
    } catch (err) {
      toast.error('Failed to load teachers');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTeachers();
  }, [fetchTeachers]);

  useEffect(() => {
    setCurrentPage(1);
  }, [search]);

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this teacher?')) return;
    try {
      await api.delete(`/teachers/${id}`);
      toast.success('Teacher deleted successfully');
      fetchTeachers();
    } catch (err) {
      toast.error('Failed to delete teacher');
    }
  };

  const downloadTemplate = () => {
    const csvContent = "UDISE,Title,First Name,Last Name,Gender,Date of Birth,Marital Status,Spouse Name,PAN Number,Aadhaar Number,Experience Years,Experience Months,Phone Number,Email Address,Address,Status\n07080100101,Mr,John,Doe,Male,1990-01-01,Single,,ABCDE1234F,123456789012,5,6,9876543210,john@example.com,123 Main St,Active";
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", "Teacher_Bulk_Upload_Template.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const filteredTeachers = teachers.filter(t => {
    const term = search.toLowerCase();
    return (
      (t.first_name || '').toLowerCase().includes(term) ||
      (t.last_name || '').toLowerCase().includes(term) ||
      (t.udise || '').toLowerCase().includes(term) ||
      (t.school_name || '').toLowerCase().includes(term) ||
      (t.email || '').toLowerCase().includes(term) ||
      (t.phone || '').toLowerCase().includes(term)
    );
  });

  const totalPages = Math.ceil(filteredTeachers.length / itemsPerPage);
  const paginatedTeachers = filteredTeachers.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  return (
    <AppLayout title="Teachers">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24, flexWrap: 'wrap', gap: 16 }}>
        <div style={{ position: 'relative', width: 250 }}>
          <Search size={18} color={C.g400} style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)' }} />
          <input
            type="text"
            placeholder="Search teachers..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ width: '100%', padding: '10px 14px 10px 40px', borderRadius: 10, border: `1px solid ${C.g200}`, fontSize: 14, outline: 'none' }}
          />
        </div>
        <div style={{ display: 'flex', gap: 12 }}>
          {!hideBulkAndTemplate && (
            <button
              onClick={downloadTemplate}
              style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 20px', background: '#fff', color: C.g700, border: `1px solid ${C.g200}`, borderRadius: 10, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}
            >
              <Download size={18} /> Template
            </button>
          )}
          {!hideBulkAndTemplate && (
            <button
              onClick={() => navigate('/teacher/bulk-upload')}
              style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 20px', background: '#fff', color: C.g700, border: `1px solid ${C.g200}`, borderRadius: 10, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}
            >
              <Upload size={18} /> Bulk Upload
            </button>
          )}
          <button
            onClick={() => navigate('/teacher/add')}
            style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 20px', background: C.primary, color: '#fff', border: 'none', borderRadius: 10, fontSize: 14, fontWeight: 600, cursor: 'pointer', boxShadow: '0 4px 12px rgba(79,70,229,0.2)' }}
          >
            <Plus size={18} /> Add Teacher
          </button>
        </div>
      </div>

      <div style={{ background: '#fff', borderRadius: 16, border: `1px solid ${C.g200}`, overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', minWidth: 1000 }}>
            <thead>
              <tr style={{ background: C.g50, borderBottom: `1px solid ${C.g200}` }}>
                <th style={{ padding: '14px 20px', fontSize: 12, fontWeight: 600, color: C.g500, textTransform: 'uppercase', width: 60 }}>Photo</th>
                <th style={{ padding: '14px 20px', fontSize: 12, fontWeight: 600, color: C.g500, textTransform: 'uppercase' }}>Name</th>
                <th style={{ padding: '14px 20px', fontSize: 12, fontWeight: 600, color: C.g500, textTransform: 'uppercase' }}>School Name</th>
                <th style={{ padding: '14px 20px', fontSize: 12, fontWeight: 600, color: C.g500, textTransform: 'uppercase' }}>Contact</th>
                <th style={{ padding: '14px 20px', fontSize: 12, fontWeight: 600, color: C.g500, textTransform: 'uppercase' }}>Subject</th>
                <th style={{ padding: '14px 20px', fontSize: 12, fontWeight: 600, color: C.g500, textTransform: 'uppercase', textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="6" style={{ padding: 40, textAlign: 'center', color: C.g400 }}>Loading...</td></tr>
              ) : paginatedTeachers.length === 0 ? (
                <tr>
                  <td colSpan="6" style={{ padding: 40, textAlign: 'center', color: C.g500 }}>
                    No teachers found.
                  </td>
                </tr>
              ) : (
                paginatedTeachers.map(t => (
                  <tr key={t.id} style={{ borderBottom: `1px solid ${C.g100}` }}>
                    <td style={{ padding: '12px 20px' }}>
                      <div style={{ width: 40, height: 40, borderRadius: '50%', backgroundColor: C.g100, overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        {t.profile_photo ? (
                          <img src={`${BASE_URL.replace('/api', '')}${t.profile_photo}`} alt="Profile" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        ) : (
                          <span style={{ color: C.g400, fontSize: 14, fontWeight: 600 }}>{t.first_name?.charAt(0)}</span>
                        )}
                      </div>
                    </td>
                    <td style={{ padding: '16px 20px', fontSize: 14, fontWeight: 500, color: C.g900 }}>
                      {t.title ? `${t.title} ` : ''}{t.first_name} {t.last_name}
                      <div style={{ fontSize: 12, color: C.g500, fontWeight: 400 }}>{t.gender || '-'}</div>
                    </td>
                    <td style={{ padding: '16px 20px', fontSize: 13, color: C.g600 }}>
                      <div style={{ fontWeight: 500 }}>{t.school_name || '-'}</div>
                      <div style={{ fontSize: 12, color: C.g400 }}>UDISE: {t.udise || '-'}</div>
                    </td>
                    <td style={{ padding: '16px 20px', fontSize: 13, color: C.g600 }}>
                      <div>{t.phone || '-'}</div>
                      <div>{t.email || '-'}</div>
                    </td>
                    <td style={{ padding: '16px 20px', fontSize: 13, color: C.g600 }}>{t.subject_name || '-'}</td>
                    <td style={{ padding: '16px 20px', textAlign: 'right' }}>
                      <button onClick={() => setSelectedTeacher(t)} style={{ background: 'none', border: 'none', padding: 4, cursor: 'pointer', color: C.primary, marginRight: 8 }} title="View Teacher">
                        <Eye size={16} />
                      </button>
                      <button onClick={() => navigate(`/teacher/edit/${t.id}`)} style={{ background: 'none', border: 'none', padding: 4, cursor: 'pointer', color: C.g400, marginRight: 8 }} title="Edit Teacher">
                        <Edit2 size={16} />
                      </button>
                      <button onClick={() => handleDelete(t.id)} style={{ background: 'none', border: 'none', padding: 4, cursor: 'pointer', color: '#EF4444' }} title="Delete Teacher">
                        <Trash2 size={16} />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '16px', borderTop: `1px solid ${C.g100}`, gap: 8 }}>
            <button
              disabled={currentPage === 1}
              onClick={() => setCurrentPage(p => p - 1)}
              style={{ padding: '6px 12px', border: `1px solid ${C.g200}`, background: '#fff', borderRadius: 6, cursor: currentPage === 1 ? 'not-allowed' : 'pointer', opacity: currentPage === 1 ? 0.5 : 1 }}
            >
              Prev
            </button>
            <span style={{ padding: '6px 12px', fontSize: 14, color: C.g600 }}>Page {currentPage} of {totalPages}</span>
            <button
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage(p => p + 1)}
              style={{ padding: '6px 12px', border: `1px solid ${C.g200}`, background: '#fff', borderRadius: 6, cursor: currentPage === totalPages ? 'not-allowed' : 'pointer', opacity: currentPage === totalPages ? 0.5 : 1 }}
            >
              Next
            </button>
          </div>
        )}
      </div>

      {selectedTeacher && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: 20 }}>
          <div style={{ background: '#fff', borderRadius: 16, width: '100%', maxWidth: 600, maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1), 0 10px 10px -5px rgba(0,0,0,0.04)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px 24px', borderBottom: `1px solid ${C.g100}`, position: 'sticky', top: 0, background: '#fff', zIndex: 10 }}>
              <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: C.g900 }}>Teacher Details</h2>
              <button onClick={() => setSelectedTeacher(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.g400, padding: 4 }}>
                <X size={20} />
              </button>
            </div>
            
            <div style={{ padding: 24 }}>
              <div style={{ display: 'flex', gap: 20, marginBottom: 24 }}>
                <div style={{ width: 80, height: 80, borderRadius: '50%', backgroundColor: C.g100, overflow: 'hidden', flexShrink: 0 }}>
                  {selectedTeacher.profile_photo ? (
                    <img src={`${BASE_URL.replace('/api', '')}${selectedTeacher.profile_photo}`} alt="Profile" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  ) : (
                    <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.g400, fontSize: 24, fontWeight: 600 }}>
                      {selectedTeacher.first_name?.charAt(0)}
                    </div>
                  )}
                </div>
                <div>
                  <h3 style={{ margin: '0 0 4px', fontSize: 20, fontWeight: 700, color: C.g900 }}>{selectedTeacher.title ? `${selectedTeacher.title} ` : ''}{selectedTeacher.first_name} {selectedTeacher.last_name}</h3>
                  <div style={{ fontSize: 14, color: C.g500, marginBottom: 4 }}>{selectedTeacher.subject_name || 'No Subject Assigned'}</div>
                  <div style={{ fontSize: 13, color: C.primary, fontWeight: 500 }}>{selectedTeacher.school_name || 'No School Assigned'} (UDISE: {selectedTeacher.udise || 'N/A'})</div>
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                <div>
                  <p style={{ margin: '0 0 4px', fontSize: 12, color: C.g400, fontWeight: 600, textTransform: 'uppercase' }}>Contact Info</p>
                  <p style={{ margin: 0, fontSize: 14, color: C.g800 }}>📞 {selectedTeacher.phone || 'N/A'}</p>
                  <p style={{ margin: '4px 0 0', fontSize: 14, color: C.g800 }}>✉️ {selectedTeacher.email || 'N/A'}</p>
                </div>
                <div>
                  <p style={{ margin: '0 0 4px', fontSize: 12, color: C.g400, fontWeight: 600, textTransform: 'uppercase' }}>Personal Info</p>
                  <p style={{ margin: 0, fontSize: 14, color: C.g800 }}><strong>Gender:</strong> {selectedTeacher.gender || 'N/A'}</p>
                  <p style={{ margin: '4px 0 0', fontSize: 14, color: C.g800 }}><strong>DOB:</strong> {selectedTeacher.dob ? selectedTeacher.dob.split('T')[0] : 'N/A'}</p>
                  <p style={{ margin: '4px 0 0', fontSize: 14, color: C.g800 }}><strong>Marital Status:</strong> {selectedTeacher.marital_status || 'N/A'}</p>
                  {selectedTeacher.marital_status === 'Married' && (
                    <p style={{ margin: '4px 0 0', fontSize: 14, color: C.g800 }}><strong>Spouse:</strong> {selectedTeacher.spouse_name || 'N/A'}</p>
                  )}
                </div>
                
                <div style={{ gridColumn: '1 / -1', marginTop: 8 }}>
                  <p style={{ margin: '0 0 4px', fontSize: 12, color: C.g400, fontWeight: 600, textTransform: 'uppercase' }}>Professional Details</p>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                    <p style={{ margin: 0, fontSize: 14, color: C.g800 }}><strong>PAN:</strong> {selectedTeacher.pan || 'N/A'}</p>
                    <p style={{ margin: 0, fontSize: 14, color: C.g800 }}><strong>Aadhaar:</strong> {selectedTeacher.aadhaar || 'N/A'}</p>
                    <p style={{ margin: 0, fontSize: 14, color: C.g800, gridColumn: '1 / -1' }}><strong>Experience:</strong> {selectedTeacher.experience_years || 0} Years, {selectedTeacher.experience_months || 0} Months</p>
                  </div>
                </div>

                <div style={{ gridColumn: '1 / -1', marginTop: 8 }}>
                  <p style={{ margin: '0 0 4px', fontSize: 12, color: C.g400, fontWeight: 600, textTransform: 'uppercase' }}>Address</p>
                  <p style={{ margin: 0, fontSize: 14, color: C.g800, lineHeight: 1.5 }}>{selectedTeacher.address || 'N/A'}</p>
                </div>
              </div>
            </div>
            <div style={{ padding: '16px 24px', borderTop: `1px solid ${C.g100}`, background: C.g50, display: 'flex', justifyContent: 'flex-end', borderBottomLeftRadius: 16, borderBottomRightRadius: 16 }}>
              <button onClick={() => setSelectedTeacher(null)} style={{ padding: '8px 16px', background: '#fff', border: `1px solid ${C.g200}`, borderRadius: 8, cursor: 'pointer', fontWeight: 500, color: C.g700 }}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}



    </AppLayout>
  );
};

export default TeacherList;
